<script setup lang="ts">
import type { AdminMockupSetResponse } from "mws-framethegame-ts-sdk";
import { computed, nextTick, type Ref, ref } from "vue";

import BaseButton from "@/common/components/BaseButton.vue";
import BaseIcon from "@/common/components/icons/BaseIcon.vue";
import PageWrapper from "@/common/components/PageWrapper.vue";
import TablePaginator from "@/common/components/table/TablePaginator.vue";
import TableVue from "@/common/components/table/TableVue.vue";
import type { TableColumn } from "@/common/components/table/types";
import { useTablePaginator } from "@/common/composables/useTablePaginator";
import { imgixThumbnail } from "@/common/utils/imgixThumbnail";
import MockupSetUpsertModal from "@/framing/components/MockupSetUpsertModal.vue";
import { useFrameTheGameStore } from "@/framing/store";
import { useCurrentUser } from "@/users/public/composables";

definePage({
  meta: {
    auth: ["MockupSets.Read"],
  },
});

const ftgStore = useFrameTheGameStore();
const { userHasPermission } = useCurrentUser();
const mockupSetIds: Ref<AdminMockupSetResponse["id"][]> = ref([]);

const searchMockupSets = async (
  searchTerm: Ref<string>,
  pageSize: Ref<number>,
  offset: Ref<number>,
  resultCount: Ref<number>,
) => {
  const response = await ftgStore.searchMockupSets(
    searchTerm.value,
    pageSize.value,
    offset.value,
  );

  mockupSetIds.value = response.results.map((result) => result.id);
  resultCount.value = response.resultCount;
};

const {
  searchTerm,
  pageSize,
  currentPage,
  resultCount,
  offset,
  handleSearchInputChange,
  changePage,
  onChangePageSize,
} = useTablePaginator(searchMockupSets);

const mockupSets = computed(() =>
  mockupSetIds.value
    .map((id) => ftgStore.mockupSets[id]!)
    .filter((mockupSet) => mockupSet !== undefined)
    .map((mockupSet) => {
      return {
        ...mockupSet,
      };
    }),
);

const modalOpen = ref(false);

const onSubmitMockupSet = async () => {
  await searchMockupSets(searchTerm, pageSize, offset, resultCount);
  selectedMockupSet.value = null;
  modalOpen.value = false;
};

const selectedMockupSet = ref<AdminMockupSetResponse | null>(null);
const openUpdateModal = async (mockupSet: AdminMockupSetResponse) => {
  selectedMockupSet.value = mockupSet;
  await nextTick();
  modalOpen.value = true;
};

const closeUpsertModal = async () => {
  selectedMockupSet.value = null;
  await nextTick();
  modalOpen.value = false;
};

const mockupImageKeys = [
  "frontWithSleevesImageUrl",
  "frontNoSleevesImageUrl",
  "backWithSleevesImageUrl",
  "backNoSleevesImageUrl",
] as const;

const isDefaultChecked = async (mockupSetId: string, isDefault: boolean) => {
  if (!isDefault) {
    return;
  }
  await ftgStore.markMockupSetAsDefault(mockupSetId);
  await searchMockupSets(searchTerm, pageSize, offset, resultCount);
};

const columns: TableColumn<AdminMockupSetResponse>[] = [
  {
    label: "Name",
    key: "name",
    type: "slot",
  },
  {
    label: "Assets",
    key: "hasAssets",
    type: "slot",
  },
  {
    label: "Default",
    key: "isDefault",
    type: "slot",
  },
  {
    label: "Front + sleeves",
    key: "frontWithSleevesImageUrl",
    type: "slot",
  },
  {
    label: "Front",
    key: "frontNoSleevesImageUrl",
    type: "slot",
  },
  {
    label: "Back + sleeves",
    key: "backWithSleevesImageUrl",
    type: "slot",
  },
  {
    label: "Back",
    key: "backNoSleevesImageUrl",
    type: "slot",
  },
];

searchMockupSets(searchTerm, pageSize, offset, resultCount);

const breadcrumbs = [
  {
    label: "Framing Assets",
    to: "/framing/framing-assets",
  },
];
</script>

<template>
  <PageWrapper
    title="Framing Assets"
    :breadcrumb-links="breadcrumbs"
  >
    <template #actions>
      <BaseButton
        v-if="userHasPermission('MockupSets.Write')"
        variant="primary"
        label="Create"
        @click="modalOpen = true"
      />
    </template>
    <div class="flex flex-col">
      <TableVue
        :data="mockupSets"
        :columns="columns"
        :is-searchable="true"
        @search-change="handleSearchInputChange"
      >
        <template
          v-for="label in mockupImageKeys"
          :key="label"
          #[label]="props"
        >
          <div class="w-32">
            <img
              :src="imgixThumbnail(props.item[label], { width: 80 })"
              class="w-auto max-w-16 md:max-w-20 h-auto"
              :alt="label"
            />
          </div>
        </template>
        <template #name="props">
          <a
            v-if="userHasPermission('MockupSets.Write')"
            href="#"
            class="text-indigo-600 hover:text-indigo-900 hover:underline"
            @click.prevent="openUpdateModal(props.item)"
          >
            {{ props.item.name }}
          </a>
        </template>
        <template #isDefault="props">
          <input
            v-model="props.item.isDefault"
            class="size-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-not-allowed"
            :value="props.item.isDefault"
            :disabled="true"
            type="checkbox"
            @change="isDefaultChecked(props.item.id, props.item.isDefault)"
          />
        </template>
        <template #hasAssets="props">
          <BaseIcon
            v-if="props.item.hasAsset"
            name="heroicons:check"
            class="text-green-500 size-5"
          />
          <BaseIcon
            v-else
            name="heroicons:x-mark"
            class="text-red-500 size-5"
          />
        </template>
      </TableVue>
      <TablePaginator
        :current-page="currentPage"
        :current-page-size="mockupSets.length"
        :total-count="resultCount"
        :total-paginator-cell-number="5"
        :offset="offset"
        :page-size="pageSize"
        @update:page-size="onChangePageSize"
        @page-number-changed="changePage"
      />
      <MockupSetUpsertModal
        v-if="modalOpen"
        v-model:is-open="modalOpen"
        :mockup-set="selectedMockupSet"
        @submit="onSubmitMockupSet"
        @close="closeUpsertModal"
      />
    </div>
  </PageWrapper>
</template>
